const DEFAULT_MAX_CONCURRENT_UPLOADS = 3;
const DEFAULT_UPLOAD_RETRY_ATTEMPTS = 3;
const DEFAULT_UPLOAD_RETRY_BASE_DELAY_MS = 500;

function readPositiveInteger(value: string | undefined, fallback: number) {
  const parsed = Number.parseInt(value || '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function resolveMaxConcurrentUploads() {
  return readPositiveInteger(
    import.meta.env.VITE_DOCUMENT_UPLOAD_MAX_CONCURRENT,
    DEFAULT_MAX_CONCURRENT_UPLOADS,
  );
}

export function resolveUploadRetryAttempts() {
  return readPositiveInteger(
    import.meta.env.VITE_DOCUMENT_UPLOAD_RETRY_ATTEMPTS,
    DEFAULT_UPLOAD_RETRY_ATTEMPTS,
  );
}

class UploadSemaphore {
  private active = 0;

  private readonly queue: Array<() => void> = [];

  constructor(private readonly maxConcurrent: number) {}

  private acquire() {
    if (this.active < this.maxConcurrent) {
      this.active += 1;
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      this.queue.push(() => {
        this.active += 1;
        resolve();
      });
    });
  }

  private release() {
    this.active = Math.max(0, this.active - 1);
    const next = this.queue.shift();
    if (next) {
      next();
    }
  }

  async run<T>(task: () => Promise<T>) {
    await this.acquire();

    try {
      return await task();
    } finally {
      this.release();
    }
  }
}

function getErrorStatus(error: unknown) {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const candidate = error as { status?: unknown; statusCode?: unknown; code?: unknown };
  const rawStatus = candidate.status ?? candidate.statusCode ?? candidate.code;
  const status = Number(rawStatus);
  return Number.isFinite(status) ? status : null;
}

export function isRetryableUploadError(error: unknown) {
  const status = getErrorStatus(error);
  if (status && [408, 425, 429, 500, 502, 503, 504].includes(status)) {
    return true;
  }

  const message = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : '';

  return /timeout|network|fetch|rate limit|temporarily|connection|resource exhausted|econnreset/i.test(message);
}

function wait(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export async function withUploadRetry<T>(
  task: () => Promise<T>,
  options: {
    attempts?: number;
    baseDelayMs?: number;
    shouldRetry?: (error: unknown) => boolean;
    onRetry?: (attempt: number, error: unknown) => void;
  } = {},
) {
  const attempts = options.attempts ?? resolveUploadRetryAttempts();
  const baseDelayMs = options.baseDelayMs ?? DEFAULT_UPLOAD_RETRY_BASE_DELAY_MS;
  const shouldRetry = options.shouldRetry ?? isRetryableUploadError;
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;

      if (attempt >= attempts || !shouldRetry(error)) {
        throw error;
      }

      options.onRetry?.(attempt, error);
      const jitter = Math.floor(Math.random() * 125);
      await wait(baseDelayMs * 2 ** (attempt - 1) + jitter);
    }
  }

  throw lastError;
}

export const documentUploadQueue = new UploadSemaphore(resolveMaxConcurrentUploads());
